/**
 * Column definitions for the Job CRM Contacts datatable.
 *
 * Pure presentational. Wired up by ContactsDataTable.tsx.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Archive, Link as LinkIcon, Users } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { SortableHeader } from "@/ui/composed/data-display/sortable-header";
import { cn } from "@/utils/misc";
import {
  CONTACT_TYPE_META,
  ROLE_META,
  distinctRoles,
  formatAbn,
  formatAcn,
  formatAud,
  resolveCustomRoleDisplays,
  splitExposure,
  totalExposure,
  type CustomJobRole,
  type JobContactRow,
} from "./types";

/**
 * Build the Job CRM contacts columns. Takes the org's custom-role map so the
 * Roles cell can render user-defined role badges alongside the built-in enum;
 * archived / unknown ids resolve to nothing (see `resolveCustomRoleDisplays`).
 */
export function buildContactsColumns(
  customById: ReadonlyMap<string, CustomJobRole>,
): Array<ColumnDef<JobContactRow>> {
  return [
    {
      accessorKey: "displayName",
      header: ({ column }) => <SortableHeader column={column} label="Name" />,
      meta: { label: "Name" },
      enableHiding: false,
      sortingFn: (a, b) =>
        a.original.displayName.localeCompare(b.original.displayName),
      cell: ({ row }) => {
        const contact = row.original;
        const TypeIcon = CONTACT_TYPE_META[contact.contactType].icon;
        const isArchived = contact.status === "archived";
        const hasIdentifiers = contact.abn || contact.acn;
        return (
          <div className="flex min-w-0 items-start gap-2">
            <TypeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    "truncate text-sm font-medium",
                    isArchived
                      ? "text-muted-foreground line-through"
                      : "text-foreground",
                  )}
                >
                  {contact.displayName}
                </span>
                {contact.firmContactId && (
                  <span
                    title={
                      contact.firmContact
                        ? `Linked to firm contact: ${contact.firmContact.displayName}`
                        : "Linked to firm contact"
                    }
                    className="shrink-0 text-primary/70"
                  >
                    <LinkIcon className="h-3 w-3" />
                  </span>
                )}
                {isArchived && (
                  <Badge
                    color="gray"
                    variant="soft"
                    size="sm"
                    className="gap-1"
                  >
                    <Archive className="h-3 w-3" />
                    Archived
                  </Badge>
                )}
              </div>
              {hasIdentifiers && (
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground/70">
                  {contact.acn && (
                    <span className="font-mono tabular-nums">
                      ACN {formatAcn(contact.acn)}
                    </span>
                  )}
                  {contact.acn && contact.abn && (
                    <span className="h-3 w-px bg-border" />
                  )}
                  {contact.abn && (
                    <span className="font-mono tabular-nums">
                      ABN {formatAbn(contact.abn)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "groupName",
      header: ({ column }) => <SortableHeader column={column} label="Group" />,
      meta: { label: "Group" },
      accessorFn: (row) => row.groupName ?? "",
      sortingFn: (a, b) =>
        (a.original.groupName ?? "").localeCompare(b.original.groupName ?? ""),
      cell: ({ row }) => {
        const groupName = row.original.groupName;
        if (!groupName) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <Badge
            color="blue"
            variant="soft"
            size="sm"
            className="gap-1 whitespace-nowrap"
            title={`Member of ${groupName}`}
          >
            <Users className="h-3 w-3" />
            {groupName}
          </Badge>
        );
      },
    },
    {
      id: "contactType",
      header: ({ column }) => <SortableHeader column={column} label="Type" />,
      meta: { label: "Type" },
      accessorFn: (row) => row.contactType,
      sortingFn: (a, b) =>
        CONTACT_TYPE_META[a.original.contactType].label.localeCompare(
          CONTACT_TYPE_META[b.original.contactType].label,
        ),
      cell: ({ row }) => {
        const meta = CONTACT_TYPE_META[row.original.contactType];
        const color =
          row.original.contactType === "person"
            ? "blue"
            : row.original.contactType === "authority"
              ? "amber"
              : "gray";
        return (
          <Badge color={color} variant="soft" size="sm">
            {meta.label}
          </Badge>
        );
      },
    },
    {
      id: "roles",
      meta: { label: "Roles" },
      header: ({ column }) => <SortableHeader column={column} label="Roles" />,
      accessorFn: (row) =>
        distinctRoles(row).length +
        resolveCustomRoleDisplays(row.customRoleIds, customById).length,
      sortingFn: (a, b) =>
        distinctRoles(a.original).length +
        resolveCustomRoleDisplays(a.original.customRoleIds, customById).length -
        (distinctRoles(b.original).length +
          resolveCustomRoleDisplays(b.original.customRoleIds, customById)
            .length),
      cell: ({ row }) => {
        const roles = distinctRoles(row.original);
        const customDisplays = resolveCustomRoleDisplays(
          row.original.customRoleIds,
          customById,
        );
        if (roles.length === 0 && customDisplays.length === 0) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((role) => {
              const meta = ROLE_META[role];
              return (
                <Badge
                  key={role}
                  color={meta.badgeColor}
                  variant="soft"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  {meta.label}
                </Badge>
              );
            })}
            {customDisplays.map((display) => (
              <Badge
                key={display.key}
                color={display.badgeColor}
                variant="soft"
                size="sm"
                className="whitespace-nowrap"
              >
                {display.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      id: "email",
      meta: { label: "Email" },
      header: ({ column }) => <SortableHeader column={column} label="Email" />,
      accessorFn: (row) => row.effectiveEmail ?? "",
      sortingFn: (a, b) => {
        const aVal = a.original.effectiveEmail ?? "";
        const bVal = b.original.effectiveEmail ?? "";
        if (!aVal && bVal) return 1;
        if (aVal && !bVal) return -1;
        return aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      },
      cell: ({ row }) => {
        const { effectiveEmail, emailFromFirm } = row.original;
        if (!effectiveEmail) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <span className="truncate">{effectiveEmail}</span>
            {emailFromFirm && (
              <Badge
                color="purple"
                variant="soft"
                size="sm"
                className="shrink-0 px-1 py-0 text-[9px]"
                title="Inherited from firm contact"
              >
                firm
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      id: "phone",
      meta: { label: "Phone" },
      header: ({ column }) => <SortableHeader column={column} label="Phone" />,
      accessorFn: (row) => row.effectivePhone ?? "",
      sortingFn: (a, b) => {
        const aVal = a.original.effectivePhone ?? "";
        const bVal = b.original.effectivePhone ?? "";
        if (!aVal && bVal) return 1;
        if (aVal && !bVal) return -1;
        return aVal.localeCompare(bVal);
      },
      cell: ({ row }) => {
        const { effectivePhone, phoneFromFirm } = row.original;
        if (!effectivePhone) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <span className="truncate font-mono tabular-nums">
              {effectivePhone}
            </span>
            {phoneFromFirm && (
              <Badge
                color="purple"
                variant="soft"
                size="sm"
                className="shrink-0 px-1 py-0 text-[9px]"
                title="Inherited from firm contact"
              >
                firm
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      id: "addresses",
      meta: { label: "Addresses" },
      header: ({ column }) => (
        <SortableHeader column={column} label="Addresses" />
      ),
      accessorFn: (row) => row.addresses.length,
      sortingFn: (a, b) =>
        a.original.addresses.length - b.original.addresses.length,
      cell: ({ row }) => {
        const { addresses } = row.original;
        if (addresses.length === 0) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        const first = addresses[0];
        const parts = [first.line1, first.suburb, first.state, first.postcode]
          .filter(Boolean)
          .join(", ");
        return (
          <div className="flex min-w-0 flex-col gap-0.5 text-xs text-muted-foreground">
            <span className="max-w-[200px] truncate">{parts || "Address"}</span>
            {addresses.length > 1 && (
              <span className="text-muted-foreground/60">
                +{addresses.length - 1} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "totalExposure",
      meta: { label: "Est. Exposure" },
      header: ({ column }) => (
        <SortableHeader column={column} label="Est. Exposure" />
      ),
      accessorFn: (row) => totalExposure(row),
      sortingFn: (a, b) =>
        totalExposure(a.original) - totalExposure(b.original),
      cell: ({ row }) => {
        const { creditor, debtor, unclassified } = splitExposure(row.original);
        const total = creditor + debtor + unclassified;
        if (total <= 0) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }

        const hasCreditor = creditor > 0;
        const hasDebtor = debtor > 0;

        if (!hasCreditor && !hasDebtor) {
          return (
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatAud(total)}
            </span>
          );
        }

        return (
          <div className="flex flex-col items-end gap-1">
            {hasCreditor && (
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  {formatAud(creditor)}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Owed to
                </span>
              </div>
            )}
            {hasDebtor && (
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {formatAud(debtor)}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  Owed by
                </span>
              </div>
            )}
            {unclassified > 0 && (
              <span className="text-sm tabular-nums text-muted-foreground">
                {formatAud(unclassified)}
              </span>
            )}
          </div>
        );
      },
    },
  ];
}
